// Address validation utility using postcode lookup
// For UK postcodes, uses postcodes.io API
// For other countries, you can extend this with other APIs

export interface AddressLookupResult {
  street?: string;
  city?: string;
  county?: string;
  country?: string;
  valid: boolean;
}

export const lookupAddressByPostcode = async (
  postcode: string,
  country: string = 'GB'
): Promise<AddressLookupResult> => {
  // Remove spaces and convert to uppercase for UK postcodes
  const cleanPostcode = postcode.replace(/\s+/g, '').toUpperCase();

  // UK postcode validation and lookup
  if (country.toUpperCase() === 'GB' || country.toUpperCase() === 'UK') {
    // Basic UK postcode format validation
    const ukPostcodeRegex = /^[A-Z]{1,2}[0-9][A-Z0-9]?[0-9][A-Z]{2}$/i;
    if (!ukPostcodeRegex.test(cleanPostcode)) {
      return { valid: false };
    }

    try {
      // Use postcodes.io API for UK postcodes (free, no API key required)
      const formattedPostcode = cleanPostcode.slice(0, -3) + ' ' + cleanPostcode.slice(-3);
      const response = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(formattedPostcode)}`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.status === 200 && data.result) {
          return {
            street: data.result.parish || data.result.admin_ward || '',
            city: data.result.post_town || data.result.admin_district || '',
            county: data.result.admin_county || data.result.county || '',
            country: 'GB',
            valid: true,
          };
        }
      }
    } catch (error) {
      console.error('Postcode lookup error:', error);
      // Return valid: false on error, but don't block form submission
      return { valid: false };
    }
  }

  // For other countries, return basic validation
  // You can extend this with other APIs like:
  // - US: USPS API (requires API key)
  // - Canada: Canada Post API
  // - etc.
  
  return { valid: true }; // Allow submission even if we can't validate
};

export const formatSortCode = (value: string): string => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  
  // Format as 00-00-00
  if (digits.length <= 2) {
    return digits;
  } else if (digits.length <= 4) {
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  } else {
    return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 6)}`;
  }
};

export const getLastWorkingDayOfMonth = (year: number, month: number): Date => {
  // month is 1-indexed (1 = January, 12 = December)
  // Date constructor uses 0-indexed months, so we use month (which gives us the first day of next month)
  // then subtract 1 day to get the last day of the current month
  const lastDay = new Date(year, month, 0); // Get last day of the month (day 0 of next month)
  let date = new Date(lastDay);

  // Go backwards until we find a weekday (Monday-Friday)
  // 0 = Sunday, 6 = Saturday
  while (date.getDay() === 0 || date.getDay() === 6) {
    date.setDate(date.getDate() - 1);
  }

  return date;
};

export const getLastWorkingDaysForYear = (year: number): Date[] => {
  const dates: Date[] = [];
  for (let month = 1; month <= 12; month++) {
    dates.push(getLastWorkingDayOfMonth(year, month));
  }
  return dates;
};

